import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, CheckCircle2, Circle, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { EMPLOYEE_REF_QUERY } from '@/lib/ref-defaults';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LookupField } from './lookup-field';
import { UploadField } from './upload-field';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';

type FieldType = 'checkbox' | 'text' | 'textarea' | 'number' | 'date' | 'select' | 'ref' | 'image';
type Kind = 'onboard' | 'transfer';

interface Task {
  id: string;
  pointName: string | null;
  pointDescription: string | null;
  stage: string | null;
  sortOrder: number;
  fieldType: FieldType;
  fieldOptions: string[];
  refEndpoint: string | null;
  refLabel: string | null;
  required: boolean;
  assetField: string | null;
  /** Field on the parent record this task mirrors (e.g. transfer `newSite`).
   *  For transfer reassignment refs the value lives on the record, not in an
   *  answer row — so the panel reads it from there (read-only). */
  sourceField: string | null;
  responsiblePerson?: { id?: string; fullName?: string } | null;
  otherStaff?: Array<{ id?: string; fullName?: string }> | null;
}

interface TaskAnswer {
  id: string;
  task: string | { id: string };
  value: any;
}

const DEFAULT_STAGE = 'General';

// Thin wrappers — keep the existing call sites simple.
export function OnboardingTasks({ onboardAssetId, readOnly }: { onboardAssetId: string; readOnly?: boolean }) {
  return (
    <WorkflowTasks
      kind="onboard"
      parentId={onboardAssetId}
      answersEndpoint="/onboard-asset-task-answers"
      parentBodyKey="onboardAsset"
      parentEndpoint="/onboard-assets"
      readOnly={readOnly}
    />
  );
}

export function TransferTasks({ assetTransferId, readOnly }: { assetTransferId: string; readOnly?: boolean }) {
  return (
    <WorkflowTasks
      kind="transfer"
      parentId={assetTransferId}
      answersEndpoint="/asset-transfer-task-answers"
      parentBodyKey="assetTransfer"
      parentEndpoint="/asset-transfers"
      readOnly={readOnly}
    />
  );
}

function WorkflowTasks({
  kind, parentId, answersEndpoint, parentBodyKey, parentEndpoint, readOnly = false,
}: {
  kind: Kind;
  parentId: string;
  answersEndpoint: string;
  parentBodyKey: string;
  parentEndpoint: string;
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const { employee } = useAuth();
  const currentUserId = employee?.id ?? null;

  // Strict per-task access — only the assigned responsiblePerson or anyone in
  // otherStaff can edit. No admin bypass: admins must be explicitly assigned.
  function isAllowedForTask(t: Task): boolean {
    if (readOnly) return false;
    if (!currentUserId) return false;
    if (t.responsiblePerson?.id === currentUserId) return true;
    if (Array.isArray(t.otherStaff) && t.otherStaff.some((s) => s.id === currentUserId)) return true;
    return false;
  }
  const tasksQ = useQuery({
    queryKey: [`${kind}-tasks-for-record`, parentId],
    queryFn: async () => {
      const cfgRes = await api<any[]>(`/asset-settings?kind=${kind}&limit=200`);
      const cfg = (cfgRes.data || []).find((c: any) => !!c.apply);
      if (!cfg) return { tasks: [] as Task[] };
      const tasksRes = await api<Task[]>(`/asset-setting-tasks?setting=${cfg.id}&limit=200`);
      return { tasks: tasksRes.data || [] };
    },
    enabled: !!parentId,
  });

  const answersKey = [`${kind}-task-answers`, parentId];
  const answersQ = useQuery({
    queryKey: answersKey,
    queryFn: async () => {
      const r = await api<TaskAnswer[]>(`${answersEndpoint}?${parentBodyKey}=${parentId}&limit=200`);
      return r.data || [];
    },
    enabled: !!parentId,
  });

  // Transfer "Asset Reassignment" ref tasks mirror fields on the AssetTransfer
  // record (via sourceField), not answer rows — fetch the record to show those
  // values read-only. Not needed for onboard (its ref tasks are answer-backed).
  const parentQ = useQuery({
    queryKey: [`${kind}-parent-record`, parentId],
    queryFn: async () => (await api<any>(`${parentEndpoint}/${parentId}`)).data ?? null,
    enabled: !!parentId && kind === 'transfer',
  });
  const parentRecord = parentQ.data as any;

  const upsert = useMutation({
    mutationFn: async ({ taskId, value }: { taskId: string; value: any }) => {
      const r = await api(`${answersEndpoint}/upsert`, {
        method: 'POST',
        body: JSON.stringify({ [parentBodyKey]: parentId, task: taskId, value }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onMutate: async ({ taskId, value }) => {
      await qc.cancelQueries({ queryKey: answersKey });
      const prev = qc.getQueryData<TaskAnswer[]>(answersKey);
      qc.setQueryData<TaskAnswer[]>(answersKey, (old) => {
        const list = old ? [...old] : [];
        const idx = list.findIndex((a) => taskIdOf(a) === taskId);
        if (idx >= 0) list[idx] = { ...list[idx], value };
        else list.push({ id: `tmp-${taskId}`, task: taskId, value } as any);
        return list;
      });
      return { prev };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(answersKey, ctx.prev);
      toast('Save failed', e?.message || 'Could not save the answer', 'error');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: answersKey }),
  });

  // Safety net: drop any ref task that has no refEndpoint (it can't render and
  // would fall back to a broken free-text box). The transfer reassignment refs
  // now carry endpoints (see set-transfer-ref-endpoints seed) and are valued
  // read-only from the parent record below, so they render normally.
  const tasks = (tasksQ.data?.tasks ?? []).filter((t) => !(t.fieldType === 'ref' && !t.refEndpoint));
  const answers = answersQ.data ?? [];
  const valueByTask = new Map<string, any>();
  for (const a of answers) valueByTask.set(taskIdOf(a), a.value);

  // "Doc-sourced" tasks mirror a parent-record field (transfer reassignment
  // refs) rather than an answer row — render them read-only, valued from the
  // record. Everything else is answer-backed and editable per permission.
  const isDocSourced = (t: Task) => kind === 'transfer' && t.fieldType === 'ref' && !!t.sourceField;
  const refId = (v: any): any => (v && typeof v === 'object' ? (v.id ?? v._id ?? null) : (v ?? null));
  const getTaskValue = (t: Task) => (isDocSourced(t) ? refId(parentRecord?.[t.sourceField as string]) : valueByTask.get(t.id));

  const sortedTasks = React.useMemo(
    () => [...tasks].sort((a, b) => {
      const sa = (a.stage || '').localeCompare(b.stage || '');
      if (sa !== 0) return sa;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    }),
    [tasks],
  );
  const stages = React.useMemo(() => {
    const seen = new Map<string, Task[]>();
    for (const t of sortedTasks) {
      const stage = t.stage || DEFAULT_STAGE;
      if (!seen.has(stage)) seen.set(stage, []);
      seen.get(stage)!.push(t);
    }
    for (const [, arr] of seen) arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    return [...seen.entries()].map(([name, tasks]) => ({ name, tasks }));
  }, [sortedTasks]);

  const [stepIdx, setStepIdx] = React.useState(0);
  React.useEffect(() => {
    if (stepIdx >= stages.length) setStepIdx(Math.max(0, stages.length - 1));
  }, [stages.length]);

  if (tasksQ.isLoading || answersQ.isLoading || parentQ.isLoading) {
    return <div className="text-xs text-muted-foreground py-3">Loading tasks…</div>;
  }
  if (tasks.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 px-3 border rounded-md">
        No tasks found. Activate an {kind === 'onboard' ? 'Onboarding' : 'Transferring'} Configuration with tasks under Admin → Settings.
      </div>
    );
  }

  const totalCompleted = tasks.filter((t) => isCompleted(t, getTaskValue(t))).length;
  const currentStage = stages[stepIdx];

  function stageCompleteness(stage: { tasks: Task[] }): { done: number; total: number } {
    const done = stage.tasks.filter((t) => isCompleted(t, getTaskValue(t))).length;
    return { done, total: stage.tasks.length };
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${tasks.length === 0 ? 0 : Math.round((totalCompleted / tasks.length) * 100)}%` }}
          />
        </div>
        <Badge variant={totalCompleted === tasks.length ? 'success' : 'secondary'}>
          {totalCompleted} / {tasks.length}
        </Badge>
      </div>

      <div className="flex items-center gap-1 overflow-x-auto py-1">
        {stages.map((s, i) => {
          const { done, total } = stageCompleteness(s);
          const complete = done === total;
          const active = i === stepIdx;
          return (
            <button
              key={s.name}
              onClick={() => setStepIdx(i)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs whitespace-nowrap transition ${
                active
                  ? 'bg-primary text-primary-foreground font-medium'
                  : complete
                    ? 'bg-green-100 text-green-900 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-100 dark:hover:bg-green-900/60'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              {complete ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
              <span>{i + 1}. {s.name}</span>
              <span className="opacity-70">({done}/{total})</span>
            </button>
          );
        })}
      </div>

      {currentStage && (
        <div className="space-y-2">
          <div className="text-sm font-semibold">{currentStage.name}</div>
          {currentStage.tasks.map((t) => {
            const docSourced = isDocSourced(t);
            const allowed = !docSourced && isAllowedForTask(t);
            return (
              <TaskRow
                key={t.id}
                task={t}
                value={getTaskValue(t)}
                readOnly={docSourced || !allowed}
                lockReason={docSourced ? undefined : (!allowed ? lockReasonFor(t, !!currentUserId) : undefined)}
                onCommit={(v) => upsert.mutate({ taskId: t.id, value: v })}
              />
            );
          })}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button
          size="sm" variant="outline"
          disabled={stepIdx === 0}
          onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
        </Button>
        <Button
          size="sm"
          disabled={stepIdx >= stages.length - 1}
          onClick={() => setStepIdx((i) => Math.min(stages.length - 1, i + 1))}
        >
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function taskIdOf(a: TaskAnswer): string {
  if (typeof a.task === 'string') return a.task;
  const t: any = a.task;
  if (!t) return '';
  return t.id ?? (t._id ? String(t._id) : '');
}

function lockReasonFor(t: Task, hasUser: boolean): string {
  if (!hasUser) return 'Sign in to edit';
  const allowed: string[] = [];
  if (t.responsiblePerson?.fullName) allowed.push(t.responsiblePerson.fullName);
  if (Array.isArray(t.otherStaff)) {
    for (const s of t.otherStaff) if (s.fullName) allowed.push(s.fullName);
  }
  if (allowed.length === 0) return 'No assignee on this task — assign someone in Settings';
  return `Only ${allowed.join(', ')} can edit this task`;
}

function isCompleted(t: Task, v: any): boolean {
  if (t.fieldType === 'checkbox') return !!v;
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
}

function TaskRow({
  task, value, readOnly, lockReason, onCommit,
}: {
  task: Task;
  value: any;
  readOnly: boolean;
  lockReason?: string;
  onCommit: (v: any) => void;
}) {
  const [draft, setDraft] = React.useState(value);
  React.useEffect(() => { setDraft(value); }, [value]);

  // Image answers get the full row width — the 280px answer column is
  // narrower than the 256px thumbnail plus its filename/Remove controls,
  // which would clip the preview.
  const wide = task.fieldType === 'image';

  return (
    <div className={`grid grid-cols-1 ${wide ? '' : 'md:grid-cols-[1fr_280px]'} gap-3 items-start border rounded-md p-3 ${readOnly ? 'opacity-90' : ''}`}>
      <div className="text-sm">
        <div className="flex items-center gap-2 font-medium">
          {readOnly && <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
          <span>{task.pointName || <em>untitled task</em>}</span>
          {task.required && <span className="text-destructive ml-1">*</span>}
        </div>
        {task.pointDescription && (
          <div className="text-xs text-muted-foreground mt-0.5">{task.pointDescription}</div>
        )}
        <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
          {task.responsiblePerson?.fullName && (
            <span>Responsible: <span className="font-medium text-foreground">{task.responsiblePerson.fullName}</span></span>
          )}
          {Array.isArray(task.otherStaff) && task.otherStaff.length > 0 && (
            <span>· Other staff: {task.otherStaff.map((s) => s.fullName).filter(Boolean).join(', ')}</span>
          )}
        </div>
        {lockReason && (
          <div className="text-xs text-muted-foreground mt-1 italic">{lockReason}</div>
        )}
      </div>
      <div>
        <Label className="text-xs sr-only">Answer</Label>
        <TaskInput task={task} draft={draft} setDraft={setDraft} onCommit={onCommit} readOnly={readOnly} />
      </div>
    </div>
  );
}

function TaskInput({
  task, draft, setDraft, onCommit, readOnly,
}: {
  task: Task;
  draft: any;
  setDraft: (v: any) => void;
  onCommit: (v: any) => void;
  readOnly: boolean;
}) {
  if (task.fieldType === 'checkbox') {
    return (
      <div className="flex items-center h-9">
        <Checkbox
          checked={!!draft}
          disabled={readOnly}
          onCheckedChange={(c) => { setDraft(!!c); onCommit(!!c); }}
        />
      </div>
    );
  }
  if (task.fieldType === 'select') {
    return (
      <Select
        value={(draft as string) ?? ''}
        onValueChange={(v) => { setDraft(v); onCommit(v); }}
        disabled={readOnly}
      >
        <SelectTrigger className="h-9"><SelectValue placeholder="— Select —" /></SelectTrigger>
        <SelectContent>
          {(task.fieldOptions ?? []).map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (task.fieldType === 'image') {
    // Same upload widget the wizard uses for image tasks (see
    // onboard-wizard.tsx) — the answer value is the S3 key, and UploadField
    // resolves it to a signed preview URL on mount. Without this branch the
    // task fell through to the text <Input> below and showed the raw key
    // instead of the photo. UploadField takes no `disabled` prop, so
    // read-only renders are locked with a pointer-events shell.
    return (
      <div className={readOnly ? 'opacity-50 pointer-events-none' : ''}>
        <UploadField
          f={{ name: 'photo', label: task.pointName ?? 'Photo', type: 'image', uploadPrefix: 'asset' }}
          value={typeof draft === 'string' ? draft : null}
          onChange={(key) => { setDraft(key); onCommit(key); }}
        />
      </div>
    );
  }
  if (task.fieldType === 'ref' && task.refEndpoint) {
    return (
      <RefInput
        endpoint={task.refEndpoint}
        labelKey={task.refLabel || 'name'}
        value={draft}
        readOnly={readOnly}
        onChange={(v) => { setDraft(v); onCommit(v); }}
      />
    );
  }
  return (
    <Input
      type={task.fieldType === 'number' ? 'number' : task.fieldType === 'date' ? 'date' : 'text'}
      value={
        task.fieldType === 'date' && draft
          ? String(draft).slice(0, 10)
          : ((draft ?? '') as any)
      }
      disabled={readOnly}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const v = task.fieldType === 'number'
          ? (draft === '' || draft == null ? null : Number(draft))
          : (draft || null);
        onCommit(v);
      }}
    />
  );
}

function RefInput({
  endpoint, labelKey, value, readOnly, onChange,
}: {
  endpoint: string;
  labelKey: string;
  value: unknown;
  readOnly: boolean;
  onChange: (v: string | null) => void;
}) {
  // Delegate to the shared lookup widget. The Employee active-and-sorted
  // filter is applied automatically when the picker is bound to /employees,
  // same rule as the main CrudConfig ref fields.
  const filter = endpoint === '/employees' ? (EMPLOYEE_REF_QUERY as Record<string, string>) : undefined;
  return (
    <LookupField
      endpoint={endpoint}
      labelKey={labelKey}
      value={value}
      onChange={onChange}
      filter={filter}
      disabled={readOnly}
    />
  );
}
